import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  ExternalLink, 
  Tag, 
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDiscountCodes() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedShop, setSelectedShop] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    shop_id: '',
    code: '',
    discount_text: '',
    description: '',
    affiliate_link: '',
    affiliate_network: '',
    landing_page_url: '',
    minimum_purchase: '',
    max_discount: '',
    is_verified: false
  });

  const affiliateNetworks = [
    { value: 'partner_ads', label: 'Partner Ads' },
    { value: 'adtraction', label: 'Adtraction' },
    { value: 'tradetracker', label: 'TradeTracker' },
    { value: 'cj_affiliate', label: 'CJ Affiliate' },
    { value: 'awin', label: 'Awin' },
    { value: 'tradedoubler', label: 'Tradedoubler' }
  ];

  useEffect(() => {
    // Check if user is admin
    if (!authAPI.isAdmin()) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [navigate, selectedShop, search]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('rabathub_token');
      
      // Fetch shops
      const shopsRes = await fetch(`${API_URL}/api/shops?limit=100`);
      const shopsData = await shopsRes.json();
      setShops(shopsData.shops || []);
      
      // Fetch discount codes
      let url = `${API_URL}/api/discount-codes?limit=100`;
      if (selectedShop !== 'all') url += `&shop_id=${selectedShop}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const codesRes = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const codesData = await codesRes.json();
      setCodes(codesData.codes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (code = null) => {
    if (code) {
      setEditingCode(code);
      setFormData({
        shop_id: code.shop_id,
        code: code.code,
        discount_text: code.discount_text,
        description: code.description || '',
        affiliate_link: code.affiliate_link || '',
        affiliate_network: code.affiliate_network || '',
        landing_page_url: code.landing_page_url || '',
        minimum_purchase: code.minimum_purchase?.toString() || '',
        max_discount: code.max_discount?.toString() || '',
        is_verified: code.is_verified || false
      });
    } else {
      setEditingCode(null);
      setFormData({
        shop_id: selectedShop !== 'all' ? selectedShop : '',
        code: '',
        discount_text: '',
        description: '',
        affiliate_link: '',
        affiliate_network: '',
        landing_page_url: '',
        minimum_purchase: '',
        max_discount: '',
        is_verified: false
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.shop_id || !formData.code || !formData.discount_text) {
      toast.error('Udfyld venligst alle påkrævede felter');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('rabathub_token');
      const payload = {
        ...formData,
        minimum_purchase: formData.minimum_purchase ? parseFloat(formData.minimum_purchase) : null,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null
      };

      const url = editingCode 
        ? `${API_URL}/api/discount-codes/${editingCode.id}`
        : `${API_URL}/api/discount-codes`;
      
      const res = await fetch(url, {
        method: editingCode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Fejl ved gemning');
      }

      toast.success(editingCode ? 'Rabatkode opdateret!' : 'Rabatkode oprettet!');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne rabatkode?')) return;
    
    try {
      const token = localStorage.getItem('rabathub_token');
      const res = await fetch(`${API_URL}/api/discount-codes/${codeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Kunne ikke slette');

      toast.success('Rabatkode slettet');
      fetchData();
    } catch (error) {
      toast.error('Fejl ved sletning');
    }
  };

  const handleToggle = async (codeId) => {
    try {
      const token = localStorage.getItem('rabathub_token');
      const res = await fetch(`${API_URL}/api/discount-codes/${codeId}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);

      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error('Fejl ved ændring af status');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbage
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Rabatkoder</h1>
              <p className="text-gray-600">Administrer rabatkoder og affiliate links</p>
            </div>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-teal-500 to-cyan-600"
            data-testid="add-code-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ny Rabatkode
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Søg efter kode eller beskrivelse..."
                    className="pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={selectedShop} onValueChange={setSelectedShop}>
                <SelectTrigger className="w-full md:w-64" data-testid="shop-filter">
                  <SelectValue placeholder="Filtrer efter butik" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle butikker</SelectItem>
                  {shops.map(shop => (
                    <SelectItem key={shop.id} value={shop.id}>{shop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Codes List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : codes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen rabatkoder</h3>
              <p className="text-gray-500 mb-4">Opret din første rabatkode for at komme i gang</p>
              <Button onClick={() => handleOpenDialog()} data-testid="add-first-code-btn">
                <Plus className="w-4 h-4 mr-2" />
                Opret Rabatkode
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {codes.map(code => (
              <Card key={code.id} className={`${!code.is_active ? 'opacity-60' : ''}`} data-testid={`code-card-${code.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {code.shop_logo && (
                        <img 
                          src={code.shop_logo} 
                          alt={code.shop_name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-lg bg-teal-100 text-teal-800 px-3 py-1 rounded">
                            {code.code}
                          </span>
                          <span className="text-gray-600">{code.discount_text}</span>
                          {code.is_verified && (
                            <CheckCircle className="w-4 h-4 text-green-500" title="Verificeret" />
                          )}
                          {!code.is_active && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inaktiv</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {code.shop_name} • {code.affiliate_network || 'Ikke tilknyttet'}
                          {code.usage_count > 0 && ` • ${code.usage_count} brug`}
                        </div>
                        {code.affiliate_link && (
                          <a 
                            href={code.affiliate_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-teal-600 hover:underline flex items-center gap-1 mt-1"
                          >
                            <LinkIcon className="w-3 h-3" />
                            Affiliate link
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={code.is_active}
                        onCheckedChange={() => handleToggle(code.id)}
                        data-testid={`toggle-${code.id}`}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenDialog(code)}
                        data-testid={`edit-${code.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(code.id)}
                        data-testid={`delete-${code.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCode ? 'Rediger Rabatkode' : 'Opret Ny Rabatkode'}
              </DialogTitle>
              <DialogDescription>
                Udfyld informationerne nedenfor for at {editingCode ? 'opdatere' : 'oprette'} rabatkoden
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shop_id">Butik *</Label>
                  <Select 
                    value={formData.shop_id} 
                    onValueChange={(v) => setFormData({...formData, shop_id: v})}
                    disabled={!!editingCode}
                  >
                    <SelectTrigger data-testid="dialog-shop-select">
                      <SelectValue placeholder="Vælg butik" />
                    </SelectTrigger>
                    <SelectContent>
                      {shops.map(shop => (
                        <SelectItem key={shop.id} value={shop.id}>{shop.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="code">Rabatkode *</Label>
                  <Input
                    id="code"
                    placeholder="F.eks. SPAR20"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    data-testid="dialog-code-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="discount_text">Rabattekst *</Label>
                <Input
                  id="discount_text"
                  placeholder="F.eks. 20% rabat på alt"
                  value={formData.discount_text}
                  onChange={(e) => setFormData({...formData, discount_text: e.target.value})}
                  data-testid="dialog-discount-text-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Beskrivelse</Label>
                <Input
                  id="description"
                  placeholder="Yderligere detaljer om rabatkoden"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Affiliate Information
                </h4>
                
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="affiliate_link">Affiliate Link</Label>
                    <Input
                      id="affiliate_link"
                      placeholder="https://track.partner-ads.com/..."
                      value={formData.affiliate_link}
                      onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
                      data-testid="dialog-affiliate-link-input"
                    />
                    <p className="text-xs text-gray-500">Det fulde tracking link fra dit affiliate netværk</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="affiliate_network">Affiliate Netværk</Label>
                      <Select 
                        value={formData.affiliate_network} 
                        onValueChange={(v) => setFormData({...formData, affiliate_network: v})}
                      >
                        <SelectTrigger data-testid="dialog-network-select">
                          <SelectValue placeholder="Vælg netværk" />
                        </SelectTrigger>
                        <SelectContent>
                          {affiliateNetworks.map(network => (
                            <SelectItem key={network.value} value={network.value}>
                              {network.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landing_page_url">Landing Page URL</Label>
                      <Input
                        id="landing_page_url"
                        placeholder="https://shop.dk/tilbud"
                        value={formData.landing_page_url}
                        onChange={(e) => setFormData({...formData, landing_page_url: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium text-gray-900 mb-3">Betingelser</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minimum_purchase">Minimumskøb (kr)</Label>
                    <Input
                      id="minimum_purchase"
                      type="number"
                      placeholder="F.eks. 200"
                      value={formData.minimum_purchase}
                      onChange={(e) => setFormData({...formData, minimum_purchase: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="max_discount">Maks rabat (kr)</Label>
                    <Input
                      id="max_discount"
                      type="number"
                      placeholder="F.eks. 500"
                      value={formData.max_discount}
                      onChange={(e) => setFormData({...formData, max_discount: e.target.value})}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <Switch
                    id="is_verified"
                    checked={formData.is_verified}
                    onCheckedChange={(v) => setFormData({...formData, is_verified: v})}
                  />
                  <Label htmlFor="is_verified" className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Verificeret (koden er testet og virker)
                  </Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Annuller
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-gradient-to-r from-teal-500 to-cyan-600"
                data-testid="dialog-save-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingCode ? 'Gem Ændringer' : 'Opret Rabatkode'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
